import {
  DndContext,
  DragOverlay,
  KeyboardCode,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
} from "@dnd-kit/core";
import { Badge, Caption1, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import { ReOrderDotsVertical16Regular } from "@fluentui/react-icons";
import { useMemo, useState, type ReactNode } from "react";
import { SectionTitle } from "@/components/ui-typography";
import type { EmploymentType, OrganizationTeam, UserResource } from "@/engine/types";
import type { ResourceWorkloadSummary } from "@/store/catalog-store";
import { ResourcePersonCard } from "./ResourcePersonCard";
import { TeamMark } from "./team-appearance";

export type BoardAxis = "team" | "employment" | "grade" | "project";

export type BoardGroup = {
  key: string;
  label: string;
  color?: string;
  icon?: string;
  items: UserResource[];
};

const useStyles = makeStyles({
  lanes: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    overflowX: "auto",
    paddingBottom: tokens.spacingVerticalM,
    alignItems: "flex-start",
    minHeight: "320px",
  },
  column: {
    width: "290px",
    minWidth: "290px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.border("1px", "solid", "transparent"),
  },
  columnOver: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `2px ${tokens.spacingHorizontalXS}`,
  },
  title: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minHeight: "72px",
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalS}`,
  },
  grip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "grab",
    ":hover": {
      backgroundColor: tokens.colorSubtleBackgroundHover,
      color: tokens.colorNeutralForeground2,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    ":active": { cursor: "grabbing" },
  },
  dragging: {
    opacity: 0.35,
  },
  overlay: {
    width: "274px",
  },
});

const laneKeyboardCoordinates: KeyboardCoordinateGetter = (event, { context }) => {
  const { collisionRect, droppableRects, droppableContainers } = context;
  if (!collisionRect) return undefined;
  if (event.code !== KeyboardCode.Left && event.code !== KeyboardCode.Right) return undefined;
  const towardRight = event.code === KeyboardCode.Right;
  const lanes: Array<{ left: number; width: number; top: number }> = [];
  droppableContainers.getEnabled().forEach((entry) => {
    if (!String(entry.id).startsWith("lane:")) return;
    const rect = droppableRects.get(entry.id);
    if (!rect) return;
    lanes.push(rect);
  });
  lanes.sort((a, b) => a.left - b.left);
  if (lanes.length === 0) return undefined;
  const center = collisionRect.left + collisionRect.width / 2;
  let index = lanes.findIndex((lane) => center >= lane.left && center <= lane.left + lane.width);
  if (index < 0) {
    index = lanes.reduce((best, lane, i) => (Math.abs(lane.left - center) < Math.abs(lanes[best]!.left - center) ? i : best), 0);
  }
  const next = lanes[index + (towardRight ? 1 : -1)];
  if (!next) return undefined;
  event.preventDefault();
  return { x: next.left + 12, y: next.top + 28 };
};

function laneKeyOf(id: string | number): string | null {
  const value = String(id);
  return value.startsWith("lane:") ? value.slice("lane:".length) : null;
}

export function ResourceBoard({
  groups,
  axis,
  teams,
  workloads,
  masked,
  onMove,
  onOpen,
  onEdit,
  onDelete,
  onOpenProject,
}: {
  groups: BoardGroup[];
  axis: BoardAxis;
  teams: Record<string, OrganizationTeam>;
  workloads: Record<string, ResourceWorkloadSummary>;
  masked: boolean;
  onMove: (resourceId: string, groupKey: string) => void;
  onOpen: (resource: UserResource) => void;
  onEdit: (resource: UserResource) => void;
  onDelete: (resource: UserResource) => void;
  onOpenProject: (projectId: string) => void;
}) {
  const styles = useStyles();
  const canDrag = axis !== "project";
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: laneKeyboardCoordinates }),
  );
  const people = useMemo(() => {
    const map = new Map<string, UserResource>();
    for (const group of groups) for (const resource of group.items) map.set(resource.id, resource);
    return map;
  }, [groups]);
  const labels = useMemo(() => new Map(groups.map((group) => [group.key, group.label || "미지정"])), [groups]);

  const announcements: Announcements = {
    onDragStart({ active }) {
      const name = people.get(String(active.id))?.displayName ?? "인력";
      return `「${name}」을(를) 들었습니다. 좌우 화살표로 레인을 옮기고 스페이스로 놓으세요.`;
    },
    onDragOver({ over }) {
      const key = over ? laneKeyOf(over.id) : null;
      return key ? labels.get(key) : undefined;
    },
    onDragEnd({ active, over }) {
      const name = people.get(String(active.id))?.displayName ?? "인력";
      const key = over ? laneKeyOf(over.id) : null;
      return key ? `「${name}」을(를) ${labels.get(key) ?? "레인"}에 놓았습니다.` : `「${name}」 이동을 취소했습니다.`;
    },
    onDragCancel({ active }) {
      const name = people.get(String(active.id))?.displayName ?? "인력";
      return `「${name}」 이동을 취소했습니다.`;
    },
  };

  const onDragStart = ({ active }: DragStartEvent) => setActiveId(String(active.id));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    const key = over ? laneKeyOf(over.id) : null;
    if (!key || !canDrag) return;
    onMove(String(active.id), key);
  };
  const onDragCancel = () => setActiveId(null);

  const active = activeId ? people.get(activeId) : undefined;

  const lanes = (
    <div className={styles.lanes}>
      {groups.map((group) => (
        <Lane key={group.key} group={group} canDrag={canDrag}>
          {group.items.map((resource) => (
            <BoardCard
              key={resource.id}
              resource={resource}
              team={resource.teamId ? teams[resource.teamId] : undefined}
              workload={workloads[resource.id]!}
              masked={masked}
              canDrag={canDrag}
              axis={axis}
              dragging={activeId === resource.id}
              onOpen={() => onOpen(resource)}
              onEdit={() => onEdit(resource)}
              onDelete={() => onDelete(resource)}
              onOpenProject={onOpenProject}
            />
          ))}
          {group.items.length === 0 && <Caption1 className={styles.empty}>{canDrag ? "여기로 옮기기" : "인력이 없습니다"}</Caption1>}
        </Lane>
      ))}
    </div>
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable: "옮기기 버튼에서 스페이스로 들고, 좌우 화살표로 레인을 고른 뒤 스페이스로 놓습니다. Esc로 취소합니다.",
        },
      }}
    >
      {lanes}
      <DragOverlay>
        {canDrag && active ? (
          <div className={styles.overlay}>
            <ResourcePersonCard
              resource={active}
              team={active.teamId ? teams[active.teamId] : undefined}
              workload={workloads[active.id]!}
              masked={masked}
              layout="board"
              showTeam={axis !== "team"}
              showEmployment={axis !== "employment"}
              showGrade={axis !== "grade"}
              onOpen={() => undefined}
              onEdit={() => undefined}
              onDelete={() => undefined}
              onOpenProject={() => undefined}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function Lane({ group, canDrag, children }: { group: BoardGroup; canDrag: boolean; children: ReactNode }) {
  const styles = useStyles();
  const { setNodeRef, isOver } = useDroppable({ id: `lane:${group.key}`, disabled: !canDrag });
  return (
    <section ref={setNodeRef} className={mergeClasses(styles.column, isOver && styles.columnOver)} aria-label={group.label || "미지정"}>
      <header className={styles.head}>
        {(group.icon || group.color) && <TeamMark icon={group.icon} color={group.color} />}
        <SectionTitle className={styles.title} title={group.label || "미지정"}>
          {group.label || "미지정"}
        </SectionTitle>
        <Badge appearance="tint" color="subtle" size="small">
          {group.items.length}
        </Badge>
      </header>
      <div className={styles.list}>{children}</div>
    </section>
  );
}

function BoardCard({
  resource,
  team,
  workload,
  masked,
  canDrag,
  axis,
  dragging,
  onOpen,
  onEdit,
  onDelete,
  onOpenProject,
}: {
  resource: UserResource;
  team?: OrganizationTeam;
  workload: ResourceWorkloadSummary;
  masked: boolean;
  canDrag: boolean;
  axis: BoardAxis;
  dragging: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenProject: (projectId: string) => void;
}) {
  const styles = useStyles();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({ id: resource.id, disabled: !canDrag });
  return (
    <div ref={setNodeRef} className={dragging ? styles.dragging : undefined}>
      <ResourcePersonCard
        resource={resource}
        team={team}
        workload={workload}
        masked={masked}
        layout="board"
        showTeam={axis !== "team"}
        showEmployment={axis !== "employment"}
        showGrade={axis !== "grade"}
        onOpen={onOpen}
        onEdit={onEdit}
        onDelete={onDelete}
        onOpenProject={onOpenProject}
        dragHandle={
          canDrag ? (
            <button
              type="button"
              ref={setActivatorNodeRef}
              className={styles.grip}
              aria-label={`${resource.displayName} 레인 옮기기`}
              {...attributes}
              {...listeners}
            >
              <ReOrderDotsVertical16Regular />
            </button>
          ) : undefined
        }
      />
    </div>
  );
}

export function boardAxisOf(groupBy: "none" | BoardAxis): BoardAxis {
  return groupBy === "none" ? "team" : groupBy;
}

export function moveResourceOnBoard(axis: BoardAxis, resourceId: string, groupKey: string, update: (id: string, patch: Partial<UserResource>) => void) {
  if (axis === "team") update(resourceId, { teamId: groupKey === "__none" ? undefined : groupKey });
  else if (axis === "grade") update(resourceId, { grade: groupKey as UserResource["grade"] });
  else if (axis === "employment") update(resourceId, { employmentType: groupKey as EmploymentType });
}
